import { Injectable } from '@angular/core';

import { TagLocation, TagNode } from '@mhe/reader/models';
import { derivePageScopeKey, TopicTocReverseMap } from './topic-toc-reverse-map';

import { TopicResolutionHints, resolveMatchedTopicNodes } from '../topics-metadata.resolver';

interface TopicMetadataContext {
  topicsTree: unknown
  reverseMap: TopicTocReverseMap | undefined
  selectedLocation: TagLocation | null
  hints?: TopicResolutionHints
}

export interface TopicMetadataSnapshot {
  pageTopics: TagNode[]
  lastEvaluatedSpineId: string | null
  lastEvaluatedCfi: string | null
  lastEvaluatedPageScopeKey: string | null
  lastUpdatedAt: number
}

@Injectable({
  providedIn: 'root',
})
export class TopicMetadataStore {
  private currentSnapshot: TopicMetadataSnapshot = {
    pageTopics: [],
    lastEvaluatedSpineId: null,
    lastEvaluatedCfi: null,
    lastEvaluatedPageScopeKey: null,
    lastUpdatedAt: 0,
  };

  private lastContext: TopicMetadataContext | null = null;

  private isDebugMode(): boolean {
    return typeof window !== 'undefined' && localStorage.getItem('readerx_debug') === 'true';
  }

  updateFromNavigationContext(
    topicsTree: unknown,
    reverseMap: TopicTocReverseMap | undefined,
    selectedLocation: TagLocation | null,
    currentSpineId: string,
    hints?: TopicResolutionHints,
  ): TagNode[] {
    const evaluatedCfi = selectedLocation?.cfi || hints?.targetCfi || null;
    const evaluatedPageScopeKey = derivePageScopeKey(evaluatedCfi || undefined, currentSpineId);
    const hasResolvableContext = Boolean(
      selectedLocation?.cfi ||
      selectedLocation?.anchorUuid ||
      hints?.targetCfi ||
      hints?.targetAnchorUuid ||
      hints?.targetSpineId,
    );

    if (!hasResolvableContext && this.currentSnapshot.lastEvaluatedSpineId === currentSpineId) {
      return this.currentSnapshot.pageTopics;
    }

    const pageTopics = resolveMatchedTopicNodes(
      topicsTree,
      selectedLocation,
      currentSpineId,
      hints,
      reverseMap,
    );

    this.lastContext = {
      topicsTree,
      reverseMap,
      selectedLocation,
      hints,
    };

    const shouldKeepPreviousTopic =
      pageTopics.length === 0 &&
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      this.currentSnapshot.pageTopics.length > 0;

    const nextPageTopics = shouldKeepPreviousTopic
      ? this.currentSnapshot.pageTopics
      : pageTopics;

    const currentIds = this.currentSnapshot.pageTopics.map((node) => node.id).sort();
    const nextIds = nextPageTopics.map((node) => node.id).sort();
    const didTopicsChange =
      currentIds.length !== nextIds.length ||
      currentIds.some((id, index) => id !== nextIds[index]);

    const didMeaningfullyChange =
      this.currentSnapshot.lastEvaluatedSpineId !== (currentSpineId || null) ||
      this.currentSnapshot.lastEvaluatedCfi !== evaluatedCfi ||
      this.currentSnapshot.lastEvaluatedPageScopeKey !== (evaluatedPageScopeKey || null) ||
      didTopicsChange;

    if (!didMeaningfullyChange) {
      return this.currentSnapshot.pageTopics;
    }

    this.currentSnapshot = {
      pageTopics: nextPageTopics,
      lastEvaluatedSpineId: currentSpineId || null,
      lastEvaluatedCfi: evaluatedCfi,
      lastEvaluatedPageScopeKey: evaluatedPageScopeKey || null,
      lastUpdatedAt: Date.now(),
    };

    if (this.isDebugMode()) {
      console.log('[TopicMetadataStore] matchedPageTopics', this.currentSnapshot);
    }

    return nextPageTopics;
  }

  getCurrentMetadata(): TagNode[] {
    return this.currentSnapshot.pageTopics;
  }

  getCurrentSnapshot(): TopicMetadataSnapshot {
    return this.currentSnapshot;
  }

  /**
   * Check if the store has evaluated the location's page scope.
   * Returns true if metadata resolution has completed for the same derived page,
   * false if location hasn't been evaluated yet or evaluation is pending.
   */
  isLocationEvaluated(spineId: string, cfi: string): boolean {
    const requestedPageScopeKey = derivePageScopeKey(cfi, spineId);

    return (
      this.currentSnapshot.lastEvaluatedSpineId === spineId &&
      this.currentSnapshot.lastEvaluatedPageScopeKey !== null &&
      this.currentSnapshot.lastEvaluatedPageScopeKey === requestedPageScopeKey
    );
  }

  resolveForLocation(currentSpineId: string, currentCfi: string): TopicMetadataSnapshot {
    const currentPageScopeKey = derivePageScopeKey(currentCfi, currentSpineId);

    // Only trust the cached snapshot when it was evaluated for this exact page scope.
    // A prior "same spine, non-empty topics" shortcut used to live here, but a single
    // spine item can span multiple topic sections (e.g. two TOC sub-headings on one
    // XHTML page). That shortcut let getLocation() hand FA4 whatever topics were last
    // resolved for *any* location on the spine - including a different section than the
    // one currently requested - so the same page could non-deterministically report a
    // different pageTopics count across visits depending on prior scroll/nav history.
    const isExactFresh =
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      this.currentSnapshot.lastEvaluatedPageScopeKey === currentPageScopeKey;

    if (isExactFresh) {
      return this.currentSnapshot;
    }

    if (!this.lastContext) {
      this.currentSnapshot = {
        pageTopics: [],
        lastEvaluatedSpineId: currentSpineId || null,
        lastEvaluatedCfi: currentCfi || null,
        lastEvaluatedPageScopeKey: currentPageScopeKey || null,
        lastUpdatedAt: Date.now(),
      };

      return this.currentSnapshot;
    }

    const refreshedHints: TopicResolutionHints = {
      ...(this.lastContext.hints || {}),
      targetSpineId: currentSpineId,
    };

    const selectedLocationForSpine =
      this.lastContext.selectedLocation?.spineId === currentSpineId
        ? this.lastContext.selectedLocation
        : null;

    const pageTopics = resolveMatchedTopicNodes(
      this.lastContext.topicsTree,
      selectedLocationForSpine,
      currentSpineId,
      refreshedHints,
      this.lastContext.reverseMap,
    );

    // Do not overwrite a known good topic on the same spine with a null recompute result.
    const shouldKeepPreviousTopic =
      pageTopics.length === 0 &&
      this.currentSnapshot.lastEvaluatedSpineId === currentSpineId &&
      this.currentSnapshot.pageTopics.length > 0;

    this.currentSnapshot = {
      pageTopics: shouldKeepPreviousTopic ? this.currentSnapshot.pageTopics : pageTopics,
      lastEvaluatedSpineId: currentSpineId || null,
      lastEvaluatedCfi: currentCfi || null,
      lastEvaluatedPageScopeKey: currentPageScopeKey || null,
      lastUpdatedAt: Date.now(),
    };

    return this.currentSnapshot;
  }
}
export type { TopicResolutionHints };
